import React, { useState } from "react";
import { motion, AnimatePresence } from "framer-motion";
import { ArrowDown } from "lucide-react";
import { SYSTEM_FINDER as F, CTA_TEXT } from "../data";

const Choice = ({ active, onClick, children, testid }) => (
  <button type="button" data-testid={testid} onClick={onClick} aria-pressed={active}
    className={`rounded-sm border px-5 py-2.5 text-sm transition-colors ${active ? "bg-[#172B38] text-white border-[#172B38]" : "bg-white text-[#3E474B] border-[#D9D6CC] hover:border-[#344C59]"}`}>
    {children}
  </button>
);

const pick = (source, size) => {
  if (source === "well") return F.results.well;
  if (!size) return null;
  return F.sizes.find((s) => s.id === size).big ? F.results.big : F.results.standard;
};

export default function SystemFinder() {
  const [source, setSource] = useState("");
  const [size, setSize] = useState("");
  const result = pick(source, size);

  return (
    <section data-testid="system-finder" className="mb-4">
      <div className="bg-white border border-[#D9D6CC] p-7 md:p-12">
        <h2 className="font-heading font-normal text-[2rem] md:text-[2.4rem] text-[#172B38] leading-tight">{F.heading}</h2>
        <p className="text-[#3E474B] mt-3">{F.sub}</p>

        <p className="font-medium text-[#172B38] mt-8 text-sm">1. Where does your water come from?</p>
        <div className="flex flex-wrap gap-3 mt-3">
          <Choice testid="finder-source-city" active={source === "city"} onClick={() => setSource("city")}>City water</Choice>
          <Choice testid="finder-source-well" active={source === "well"} onClick={() => setSource("well")}>Well water</Choice>
        </div>

        {source === "city" && (
          <>
            <p className="font-medium text-[#172B38] mt-7 text-sm">2. How many people live in your home?</p>
            <div className="flex flex-wrap gap-3 mt-3">
              {F.sizes.map((s) => (
                <Choice key={s.id} testid={`finder-size-${s.id}`} active={size === s.id} onClick={() => setSize(s.id)}>{s.label}</Choice>
              ))}
            </div>
          </>
        )}

        <AnimatePresence mode="wait">
          {result && (
            <motion.div key={result.name} data-testid="finder-result" className="mt-8 bg-white rounded-none p-6 md:p-8 flex flex-col md:flex-row md:items-center gap-6"
              initial={{ opacity: 0, y: 12 }} animate={{ opacity: 1, y: 0 }} exit={{ opacity: 0, y: -8 }} transition={{ duration: 0.35 }}>
              <div className="flex-1">
                <span className="inline-flex text-xs font-medium text-[#344C59] border border-[#D9D6CC] px-3 py-1.5">{result.tag}</span>
                <h3 data-testid="finder-result-name" className="font-heading font-normal text-2xl text-[#172B38] mt-3">{result.name}</h3>
                <p className="text-[#3E474B] mt-2 text-[0.95rem] leading-relaxed">{result.why}</p>
              </div>
              <div className="flex flex-wrap gap-3">
                <button type="button" data-testid="finder-see-system" className="btn-outline"
                  onClick={() => document.getElementById(result.target)?.scrollIntoView({ behavior: "smooth" })}>
                  See this system <ArrowDown className="w-4 h-4" />
                </button>
                <a href="/#schedule" data-testid="finder-quote" className="btn-cta">{CTA_TEXT}</a>
              </div>
            </motion.div>
          )}
        </AnimatePresence>
      </div>
    </section>
  );
}
